import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, Users, Save, X, Crown, Settings, Plus, Shield } from "lucide-react";
import { RecordDetailShell } from "@/components/records/RecordDetailShell";
import { RecordSections, applicableSections } from "@/components/records/RecordSections";
import { RelationshipsPanel } from "@/components/crm/RelationshipsPanel";
import { PersonSearchSelect } from "@/components/common/PersonSearchSelect";
import { toast } from "sonner";

const ROLE_COLORS = {
  owns: "bg-amber-100 text-amber-800", producer_at: "bg-blue-100 text-blue-700",
  staff_at: "bg-violet-100 text-violet-700", protege_at: "bg-emerald-100 text-emerald-700",
  lead_at: "bg-cyan-100 text-cyan-700", contact_at: "bg-slate-100 text-slate-600",
};

export default function AgencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [layout, setLayout] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [team, setTeam] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [hideEmpty, setHideEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  const [ownerDlg, setOwnerDlg] = useState(false);
  const [memberDlg, setMemberDlg] = useState(false);

  const loadTeam = useCallback(async () => {
    const t = await api.get(`/businesses/${id}/team`);
    setTeam(t.data);
  }, [id]);

  const load = useCallback(async () => {
    try {
      const [biz, ly, cf] = await Promise.all([
        api.get(`/businesses/${id}`),
        api.get("/layouts", { params: { record_type: "business" } }),
        api.get("/custom-fields", { params: { entity_type: "business" } }),
      ]);
      setB(biz.data); setForm(biz.data);
      setLayout(ly.data); setHideEmpty(ly.data.hide_empty_default !== false);
      setCustomFields(cf.data.items.filter((f) => !f.hidden));
      await loadTeam();
    } catch (e) { toast.error(apiError(e)); navigate("/agencies"); }
  }, [id, navigate, loadTeam]);
  useEffect(() => { load(); }, [load]);
  if (!b || !layout || !team) return <PageLoader />;

  const customDefs = Object.fromEntries(customFields.map((f) => [f.name, { type: f.field_type, options: f.options }]));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/businesses/${id}`, {
        name: form.name, business_type: form.business_type, phone: form.phone, email: form.email,
        website: form.website, affiliation: form.affiliation, address: form.address, city: form.city,
        state: form.state, postal_code: form.postal_code, country: form.country, notes: form.notes,
        custom_fields: form.custom_fields || {},
      });
      setB(data); setForm(data); setEdit(false); toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  let sections = [...(layout.sections || [])];
  const placed = new Set();
  sections.forEach((s) => (s.fields || []).forEach((f) => f.source === "custom" && placed.add(f.key)));
  const unplaced = customFields.filter((f) => !placed.has(f.name));
  if (unplaced.length) sections = [...sections, { key: "additional_information", title: "Additional Information", render: "fields", columns: 2, collapsible: true, conditional: {}, fields: unplaced.map((f) => ({ key: f.name, label: f.label, source: "custom", type: f.field_type, visible: true, width: "half" })) }];
  const shown = applicableSections(sections, { hasOpportunity: false, classifications: [], canRecruiting: true });
  const systemRenderers = { relationships: () => <RelationshipsPanel bare recordType="business" recordId={id} /> };
  const canManage = team.can_manage;

  const MemberRow = ({ m, owner }) => (
    <div className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`agency-member-${m.person_id}`}>
      <div className="min-w-0">
        <button onClick={() => navigate(`/people/detail/${m.person_id}`)} className="text-left" data-testid={`agency-member-link-${m.person_id}`}>
          <span className={`text-sm ${owner ? "font-bold text-slate-900" : "text-slate-700"} hover:text-brand`}>{m.name}</span>
          {m.preferred_name && <span className="text-xs text-slate-400 ml-1">"{m.preferred_name}"</span>}
        </button>
        <div className="flex flex-wrap items-center gap-1 mt-1">
          {(m.classifications || []).slice(0, 4).map((c) => (
            <span key={c} className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{c.replace(/_/g, " ")}</span>
          ))}
          {m.contact_visible && (m.email || m.phone) && <span className="text-[11px] text-slate-400">{m.email || m.phone}</span>}
          {m.organization && <span className="text-[11px] text-slate-400">· {m.organization}</span>}
          {!m.current && <span className="text-[10px] uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">Historical</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 flex items-center gap-1 ${ROLE_COLORS[m.role_key] || "bg-slate-100 text-slate-600"}`}>
          {owner && <Crown className="w-3 h-3" />}{m.role_label}
        </span>
        {canManage && m.current && !owner && (
          <button onClick={() => endMember(m)} className="text-xs text-slate-400 hover:text-rose-600" data-testid={`agency-member-end-${m.person_id}`}>End</button>
        )}
      </div>
    </div>
  );

  const endMember = async (m) => {
    try { await api.patch(`/businesses/${id}/members/${m.relationship_id}`, { end: true }); toast.success("Marked historical"); loadTeam(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filteredMembers = (team.members || []).filter((m) => roleFilter === "all" || m.role_key === roleFilter);

  const teamNode = (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="agency-team-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-sm flex items-center gap-1.5"><Users className="w-4 h-4 text-brand" /> Agency Team</h3>
        <div className="flex items-center gap-2">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="agency-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All roles</SelectItem>
              {(team.member_role_types || []).map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {canManage && <Button size="sm" variant="outline" onClick={() => setMemberDlg(true)} data-testid="agency-add-member"><Plus className="w-4 h-4 mr-1" /> Add</Button>}
        </div>
      </div>
      {team.owner ? <div className="mb-3"><p className="text-[11px] uppercase tracking-wide text-amber-600 mb-1 flex items-center gap-1"><Crown className="w-3 h-3" /> Owner</p><MemberRow m={team.owner} owner /></div>
        : <div className="mb-3 flex items-center justify-between rounded-md border border-dashed border-amber-200 bg-amber-50/40 px-3 py-2">
            <span className="text-sm text-slate-500">No Owner assigned</span>
            {canManage && <Button size="sm" variant="outline" onClick={() => setOwnerDlg(true)} data-testid="agency-assign-owner"><Crown className="w-4 h-4 mr-1" /> Assign Owner</Button>}
          </div>}
      {filteredMembers.length ? <div className="space-y-2" data-testid="agency-members">{filteredMembers.map((m) => <MemberRow key={m.relationship_id} m={m} />)}</div>
        : <EmptyState icon={Users} title="No team members" description={canManage ? "Add People to this Agency." : "No related People yet."} />}
    </div>
  );

  const header = (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex items-start gap-4">
      <span className="w-14 h-14 rounded-lg bg-brand/10 flex items-center justify-center shrink-0"><Building2 className="w-7 h-7 text-brand" /></span>
      <div className="flex-1 min-w-0">
        <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="business-title">{b.name}</h1>
        <p className="text-sm text-slate-400 mt-0.5">{b.business_type || "—"} · {[b.city, b.state].filter(Boolean).join(", ") || "—"}</p>
        {team.owner && (
          <button onClick={() => navigate(`/people/detail/${team.owner.person_id}`)} className="mt-1.5 inline-flex items-center gap-1.5 text-sm" data-testid="agency-owner-header">
            <Crown className="w-4 h-4 text-amber-500" /><span className="text-slate-400">Primary Owner:</span>
            <span className="font-bold text-slate-800 hover:text-brand">{team.owner.name}</span>
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {canManage && !edit && (
          <Button variant="outline" size="icon" onClick={() => setShowSettings(true)} data-testid="agency-settings-gear" title="Agency Settings"><Settings className="w-4 h-4" /></Button>
        )}
        {!edit ? (canManage && <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-business">Edit</Button>) : (
          <>
            <Button variant="outline" onClick={() => { setEdit(false); setForm(b); }}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-business" className="bg-brand hover:bg-brand-strong"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </>
        )}
      </div>
    </div>
  );

  const overview = (
    <div className="space-y-5">
      {teamNode}
      <RecordSections sections={shown} record={b} custom={b.custom_fields} form={form} setForm={setForm}
        customDefs={customDefs} orgs={[]} edit={edit} hideEmpty={hideEmpty} systemRenderers={systemRenderers} />
    </div>
  );

  const related = [
    { key: "people", label: "Agency Team", icon: Users, count: (team.owner ? 1 : 0) + (team.members || []).length, node: teamNode },
    { key: "relationships", label: "Relationships", icon: Building2, node: <RelationshipsPanel recordType="business" recordId={id} /> },
  ];

  return (
    <>
      <RecordDetailShell backLabel="Agencies" onBack={() => navigate("/agencies")} header={header}
        overview={overview} related={related} hideEmpty={hideEmpty} onToggleHideEmpty={setHideEmpty} showHideEmpty={!edit} />
      {ownerDlg && <AssignPersonDialog title="Assign Owner" cta="Assign as Owner" onClose={() => setOwnerDlg(false)}
        onSubmit={async (pid) => { await api.post(`/businesses/${id}/owner`, { person_id: pid }); toast.success("Owner assigned"); setOwnerDlg(false); loadTeam(); }} />}
      {memberDlg && <AddMemberDialog roleTypes={team.member_role_types} onClose={() => setMemberDlg(false)}
        onSubmit={async (pid, role) => { await api.post(`/businesses/${id}/members`, { person_id: pid, relationship_type: role }); toast.success("Member added"); setMemberDlg(false); loadTeam(); }} />}
      {showSettings && <AgencySettings bid={id} canManageAdmins={team.can_manage_admins} onClose={() => setShowSettings(false)}
        onOwner={() => { setShowSettings(false); setOwnerDlg(true); }} onEdit={() => { setShowSettings(false); setEdit(true); }} />}
    </>
  );
}

function AssignPersonDialog({ title, cta, onClose, onSubmit }) {
  const [pid, setPid] = useState(null); const [saving, setSaving] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="assign-person-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Search an existing Person — no duplicate records are created.</DialogDescription></DialogHeader>
        <PersonSearchSelect onSelect={(p) => setPid(p?.id || null)} />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!pid || saving} data-testid="assign-person-submit" className="bg-brand hover:bg-brand-strong"
            onClick={async () => { setSaving(true); try { await onSubmit(pid); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); } }}>{cta}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMemberDialog({ roleTypes, onClose, onSubmit }) {
  const [pid, setPid] = useState(null); const [role, setRole] = useState(roleTypes[0]?.key || "producer_at"); const [saving, setSaving] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="add-member-dialog">
        <DialogHeader><DialogTitle>Add Agency Team Member</DialogTitle><DialogDescription>Relate an existing Person to this Agency by role.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Role</Label>
            <Select value={role} onValueChange={setRole}><SelectTrigger className="mt-1" data-testid="member-role"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{roleTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs font-semibold text-slate-600">Person</Label><PersonSearchSelect onSelect={(p) => setPid(p?.id || null)} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!pid || saving} data-testid="member-submit" className="bg-brand hover:bg-brand-strong"
            onClick={async () => { setSaving(true); try { await onSubmit(pid, role); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); } }}>Add Member</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgencySettings({ bid, canManageAdmins, onClose, onOwner, onEdit }) {
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [newAdmin, setNewAdmin] = useState("");
  const load = useCallback(async () => {
    try {
      const a = await api.get(`/businesses/${bid}/admins`); setAdmins(a.data.items);
      if (canManageAdmins) { const u = await api.get("/users"); setUsers(u.data.items || u.data.users || []); }
    } catch (e) { toast.error(apiError(e)); }
  }, [bid, canManageAdmins]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!newAdmin) return; try { await api.post(`/businesses/${bid}/admins`, { user_id: newAdmin }); setNewAdmin(""); toast.success("Agency Admin assigned"); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (uid) => { try { await api.delete(`/businesses/${bid}/admins/${uid}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="agency-settings-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="w-4 h-4" /> Agency Settings</DialogTitle>
          <DialogDescription>Manage this Agency's profile, owner, and scoped administrators.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onEdit} data-testid="settings-edit-profile">Edit Profile</Button>
            <Button variant="outline" size="sm" onClick={onOwner} data-testid="settings-change-owner"><Crown className="w-4 h-4 mr-1" /> Set / Change Owner</Button>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-2"><Shield className="w-3.5 h-3.5" /> Agency Admins <span className="font-normal text-slate-400">(scoped to this Agency only)</span></p>
            <div className="space-y-1.5" data-testid="agency-admins-list">
              {admins.length ? admins.map((a) => (
                <div key={a.user_id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-1.5">
                  <span className="text-sm text-slate-700">{a.name || a.email}</span>
                  {canManageAdmins && <button onClick={() => remove(a.user_id)} className="text-xs text-slate-400 hover:text-rose-600" data-testid={`remove-admin-${a.user_id}`}>Remove</button>}
                </div>
              )) : <p className="text-sm text-slate-400">No Agency Admins assigned.</p>}
            </div>
            {canManageAdmins && (
              <div className="flex gap-2 mt-2">
                <Select value={newAdmin} onValueChange={setNewAdmin}><SelectTrigger className="h-9 text-sm" data-testid="new-admin-select"><SelectValue placeholder="Select user…" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{users.filter((u) => !admins.some((a) => a.user_id === u.id)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent></Select>
                <Button size="sm" onClick={add} disabled={!newAdmin} className="bg-brand hover:bg-brand-strong" data-testid="add-admin-btn">Assign</Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
